<script lang="ts">
	import { getNumBackInUnits, sanitizeInputs, toBN } from '$lib/util/util';
	import { fly } from 'svelte/transition';
	import BigNumber from 'bignumber.js';
	import { onDestroy, onMount } from 'svelte';
	import {
		DEFAULT_STOP_LOSS_PERCENTAGE,
		LIQUIDATION_PRICE_GAP_PERCENTAGE
	} from '$lib/home/constants.js';
	import {
		positionData,
		stopLossInput,
		isStopLossInputValid,
		isStopLossInputSafe,
		stopLossWarningMessage,
		stopLossErrorMessage,
		estimatedLoss,
		lossPercentage,
		tpslPostTradeDetailsLoading,
		stopLossErrorDescription,
		stopLossRemoved
	} from '../popupsStore';
	import { marketPrices } from '$lib/util/marketHelper';
	import { marketsConfig } from '$lib/config/markets';
	import { markets } from '$lib/home/store';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import type { Market } from '$lib/types/market';

	let _stopLossInput: string;
	let _lossPercentage: string = DEFAULT_STOP_LOSS_PERCENTAGE;
	let currentMarket = $markets.find(
		(market) => market.marketIdentifier === $positionData.marketIdentifier
	) as Market;
	let currentOraclePrice: string;

	$: currentOraclePrice = $positionData.asset ? currentMarket.safePrice : '0';

	const isLong = new BigNumber($positionData.size).isPositive();

	let lossPercentageEventListner = () => {};
	let stopLossEventListner = () => {};

	onMount(() => {
		if (
			new BigNumber($positionData.takeProfit).gt(0) ||
			new BigNumber($positionData.stopLoss).gt(0)
		) {
			_stopLossInput = getNumBackInUnits($positionData.stopLoss, 18, 4);
			handleStopLossInput();
		} else {
			_lossPercentage = getDefaultStopLossPercenatge();
			lossPercentage.set(_lossPercentage);
			handleLossPercentageInput();
		}
		lossPercentageEventListner = marketPrices.subscribe((val) => {
			if (val) {
				syncLossPercentageValue();
			}
		});
		$positionData.stopLoss === '0' ? stopLossRemoved.set(true) : stopLossRemoved.set(false);
	});

	const getDefaultStopLossPercenatge = () => {
		const liquidationPrice = $positionData.liquidationPrice;
		let stopLossSafePrice;

		if (isLong) {
			stopLossSafePrice = new BigNumber(liquidationPrice)
				.multipliedBy(
					new BigNumber(1).plus(new BigNumber(LIQUIDATION_PRICE_GAP_PERCENTAGE).div(100))
				)
				.toFixed(2);
		} else {
			stopLossSafePrice = new BigNumber(liquidationPrice)
				.multipliedBy(
					new BigNumber(1).minus(new BigNumber(LIQUIDATION_PRICE_GAP_PERCENTAGE).div(100))
				)
				.toFixed(2);
		}

		const stopLossPercentage = new BigNumber(stopLossSafePrice)
			.minus(new BigNumber(currentOraclePrice))
			.abs()
			.div(new BigNumber(currentOraclePrice))
			.multipliedBy(100)
			.toFixed(2)
			.toString();
		if (new BigNumber(stopLossPercentage).lt(DEFAULT_STOP_LOSS_PERCENTAGE)) {
			return stopLossPercentage;
		}
		return DEFAULT_STOP_LOSS_PERCENTAGE;
	};

	const syncLossPercentageValue = () => {
		_lossPercentage =
			currentOraclePrice && new BigNumber(_stopLossInput).isGreaterThan(0)
				? isLong
					? new BigNumber(currentOraclePrice)
							.minus(new BigNumber(_stopLossInput).multipliedBy(1e18))
							.div(currentOraclePrice)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
					: new BigNumber(_stopLossInput)
							.multipliedBy(1e18)
							.minus(currentOraclePrice)
							.div(currentOraclePrice)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
				: '';
		lossPercentage.set(_lossPercentage);
	};

	const syncStopLossValue = () => {
		_stopLossInput = _lossPercentage
			? isLong
				? new BigNumber(1)
						.minus(new BigNumber(_lossPercentage).div(100))
						.multipliedBy(currentOraclePrice)
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals + 2)
						.toString()
				: new BigNumber(1)
						.plus(new BigNumber(_lossPercentage).div(100))
						.multipliedBy(currentOraclePrice)
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals + 2)
						.toString()
			: '';
		stopLossInput.set(toBN(_stopLossInput || '0').toString());
	};

	const handleStopLossInput = () => {
		_stopLossInput = sanitizeInputs(_stopLossInput);
		stopLossInput.set(toBN(_stopLossInput || '0').toString());
		syncLossPercentageValue();
		stopLossRemoved.set(false);
	};

	const handleLossPercentageInput = () => {
		_lossPercentage = sanitizeInputs(_lossPercentage);
		lossPercentage.set(_lossPercentage);
		syncStopLossValue();
		stopLossRemoved.set(false);
	};

	const onLossInputFocus = () => {
		lossPercentageEventListner();
		stopLossEventListner();
		stopLossEventListner = marketPrices.subscribe((val) => {
			if (val) {
				syncStopLossValue();
			}
		});
	};

	const onStopLossInputFocus = () => {
		stopLossEventListner();
		lossPercentageEventListner();
		lossPercentageEventListner = marketPrices.subscribe((val) => {
			if (val) {
				syncLossPercentageValue();
			}
		});
	};

	onDestroy(() => {
		stopLossInput.set('0');
		lossPercentage.set('0');
		lossPercentageEventListner();
		stopLossEventListner();
	});

	const removeStopLoss = () => {
		stopLossInput.set('0');
		_lossPercentage = '';
		lossPercentage.set(_lossPercentage);
		handleLossPercentageInput();
		stopLossRemoved.set(true);
	};

	let estimatedLossValue = '';
	let isLossPositive = true;

	$: if (parseFloat($estimatedLoss) >= 0) {
		estimatedLossValue = '-$' + Math.abs(parseFloat($estimatedLoss)).toFixed(2);
		isLossPositive = true;
	} else {
		estimatedLossValue = '+$' + Math.abs(parseFloat($estimatedLoss)).toFixed(2);
		isLossPositive = false;
	}

	let priceOfAsset = '0';
	$: {
		const targetMarket = $markets.find((m) => m.marketIdentifier == $positionData.marketIdentifier);
		priceOfAsset = targetMarket?.safePrice || '0';
	}
</script>

<div class="border-t border-t-fx-grey-800 w-full mb-3 mt-4" />
<div class="flex justify-between items-center">
	<div class="font-medium text-fx-grey-200 text-md mt-2">Stop Loss</div>
	{#if _lossPercentage !== ''}
		<button
			class="text-fx-blue text-sm cursor-pointer underline"
			on:click={removeStopLoss}
			on:keydown={removeStopLoss}
		>
			remove
		</button>
	{/if}
</div>
<div class="flex gap-2 mt-3">
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!$isStopLossInputValid || !$isStopLossInputSafe}
		class:border-fx-red={!$isStopLossInputValid}
		class:border-fx-yellow={$isStopLossInputValid && !$isStopLossInputSafe}
	>
		<div class="flex-1 flex">
			<span class="text-xs">$</span>

			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
				bind:value={_stopLossInput}
				on:input={handleStopLossInput}
				on:focus={onStopLossInputFocus}
			/>
		</div>

		<div class="flex items-center gap-1 justify-center">
			{#if $isStopLossInputValid && !$isStopLossInputSafe}
				<Tooltip
					title="Polynomial is not responsible if SL is too close to liquidation and/or cannot be executed due to lack of fees in margin"
					header={$stopLossWarningMessage}
				>
					<div class="text-xxs text-fx-yellow flex items-center gap-2 justify-between w-full">
						<div class="text-xs text-fx-grey-400"><span class="mr-1">|</span>SL</div>
						<img src="/caution.png" class="w-3 inline-block animate-bounce" alt="/caution.png" />
					</div>
				</Tooltip>
			{:else}
				<div class="text-xs text-fx-grey-400">
					<span class="mr-1">|</span> Stop Loss
				</div>
			{/if}
		</div>
	</div>
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!$isStopLossInputValid || !$isStopLossInputSafe}
		class:border-fx-red={!$isStopLossInputValid}
		class:border-fx-yellow={$isStopLossInputValid && !$isStopLossInputSafe}
	>
		<div class="flex-1">
			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
				bind:value={_lossPercentage}
				on:input={handleLossPercentageInput}
				on:focus={onLossInputFocus}
			/>
		</div>

		<div class="text-xs text-fx-grey-400"><span class="mr-2">|</span> Loss %</div>
	</div>
</div>
{#if !$isStopLossInputValid}
	{#if $stopLossErrorDescription != ''}
		<Tooltip bind:header={$stopLossErrorMessage} bind:title={$stopLossErrorDescription}>
			<div
				class="text-xxs text-fx-red mt-4 absolute font-semibold flex items-center gap-1 w-full ml-16"
				transition:fly={{ y: -10, duration: 200 }}
			>
				{$stopLossErrorMessage}
				<img src="/caution.png" class="w-2 inline-block" alt="/caution.png" />
			</div>
		</Tooltip>
	{:else}
		<div
			class="text-xxs text-fx-red mt-1 absolute font-semibold flex items-center gap-1 w-full"
			transition:fly={{ y: -10, duration: 200 }}
		>
			{$stopLossErrorMessage}
			<img src="/caution.png" class="w-2 inline-block" alt="/caution.png" />
		</div>
	{/if}
{/if}
<div class="flex justify-between mt-4">
	<div class="text-fx-grey-400 text-xs font-medium">
		{isLossPositive ? 'Est. Loss' : 'Est. Profit'}
	</div>
	<div class="flex gap-2">
		{#if $tpslPostTradeDetailsLoading}
			<ProcessingIcon />
		{/if}
		<div
			class="text-xs font-medium {isLossPositive ? 'text-fx-gradient-22' : 'text-fx-gradient-23'}"
		>
			{estimatedLossValue}
		</div>
	</div>
</div>
